import React, { useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import { riskOverviewSecondaryControlSx } from '../../utils/riskOverviewStyles';
import { compactIconButtonSx } from '../pos_sizing_table/tableStyles';

const OPTIONS = [
    { key: 'actual',  label: 'Act. Exp'    },
    { key: 'category',  label: 'Name' },
];
const OPTIMIZED_OPTION = { key: 'optimized', label: 'Opt. Exp' };
const ALL_OPTIONS = [...OPTIONS, OPTIMIZED_OPTION];

export default function SortControl({
    sortKey,
    sortOrder,
    onChange,
    showOptimized,
    compact = false,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const menuId = useId();
    const options = showOptimized
        ? ALL_OPTIONS
        : OPTIONS;

    useEffect(() => {
        if (!showOptimized && sortKey === 'optimized') {
            onChange('actual', 'desc');
        }
    }, [onChange, showOptimized, sortKey]);

    const handleOpen  = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleSelect = (optKey) => {
        let newOrder = sortOrder;
        if (optKey === sortKey) {
            newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            newOrder = 'desc';
        }
        onChange(optKey, newOrder);
        handleClose();
    };

    const currentLabel = ALL_OPTIONS.find(o => o.key === sortKey).label;
    const description = `Sort chart by ${currentLabel}, ${sortOrder === 'asc' ? 'ascending' : 'descending'}`;
    const menuProps = {
        'aria-controls': open ? menuId : undefined,
        'aria-expanded': open ? 'true' : undefined,
        'aria-haspopup': 'menu',
        'aria-label': description,
    };

    return (
        <>
            {compact ? (
                <Tooltip title={description}>
                    <IconButton
                        size="small"
                        onClick={handleOpen}
                        sx={compactIconButtonSx}
                        {...menuProps}
                    >
                        <SortRoundedIcon
                            sx={sortOrder === 'asc' ? { transform: 'scaleY(-1)' } : undefined}
                        />
                    </IconButton>
                </Tooltip>
            ) : (
                <Button
                    size="small"
                    onClick={handleOpen}
                    sx={riskOverviewSecondaryControlSx}
                    {...menuProps}
                    endIcon={sortOrder === 'asc'
                        ? <ArrowUpward fontSize="small" />
                        : <ArrowDownward fontSize="small" />}
                >
                    Sort by: {currentLabel}
                </Button>
            )}

            <Menu id={menuId} anchorEl={anchorEl} open={open} onClose={handleClose}>
                {options.map(({ key, label }) => (
                    <MenuItem key={key} onClick={() => handleSelect(key)}>
                        {key === sortKey && (
                            <ListItemIcon>
                                {sortOrder === 'asc'
                                    ? <ArrowUpward fontSize="small" />
                                    : <ArrowDownward fontSize="small" />}
                            </ListItemIcon>
                        )}
                        <ListItemText inset={key !== sortKey}>{label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

SortControl.propTypes = {
    sortKey:       PropTypes.oneOf(['actual','optimized','category']).isRequired,
    sortOrder:     PropTypes.oneOf(['asc','desc']).isRequired,
    onChange:      PropTypes.func.isRequired,
    showOptimized: PropTypes.bool.isRequired,
    compact:       PropTypes.bool,
};
